import { Box, useMediaQuery, useTheme } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import React, { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

import Navbar from './common/components/Navbar/Navbar';
import { useAuth } from './customHooks/useAuth';
import Routes from './routes/routes';
import theme from './theme';
import { useDiagramAuthRedirect } from './common/hooks/useDiagramAuthRedirect';

import './assets/styles/layout.css';
import Overlay from './common/components/Overlay';
import DiagramBpmnIndex from './features/BpmnIoDiagram/Index';
import { closeDiagram } from './slices/diagramViewerSlice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from './store';
import { DiagramViewerState } from './slices/diagramViewerInterface';
import { useOpportunityAuthRedirect } from './common/hooks/useOpportunityAuthRedirect';

const AppLayout: React.FC = () => {
  const location = useLocation();
  const isAuthenticated = useAuth();
  const isXPLR = location.pathname.includes('/design-nexus');
  const muiTheme = useTheme();
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));

  // Detect diagram deep links and enforce authentication while preserving
  // the intended target so users can be redirected back after login.
  useDiagramAuthRedirect();

  useOpportunityAuthRedirect();

  const showNavbarOnRoutes = [
    '/industry',
    '/process',
    '/process-xplr',
    '/solution-xplr',
    '/ideation-xplr',
    '/portfolio',
    '/design-nexus',
    '/diagram',
    '/',
    '/enterprise-context',
    '/approval-requests',
    '/ideation-coi/:id',
    '/ideation-chatbot/:id',
    '/opportunity/:id',
    '/data-xplr',
    '/data-xplr/:id',
    '/opportunity/:id/:diagramId',
  ];

  const shouldShowNavbar =
    isAuthenticated &&
    showNavbarOnRoutes.some((route) => {
      if (route.includes(':')) {
        // replace ALL route params like :id, :diagramId, etc.
        const regexPattern = '^' + route.replace(/:[^/]+/g, '[^/]+') + '$';
        const regex = new RegExp(regexPattern);
        return regex.test(location.pathname);
      }
      return location.pathname === route;
    });

  const dispatch = useDispatch();
  const { isOpen = false } = useSelector<RootState, DiagramViewerState>(
    (state) => state.diagramViewer
  );

  useEffect(() => {
    if (isOpen) {
      dispatch(closeDiagram());
    }
  }, [location.pathname]);

  return (
    <ThemeProvider theme={theme}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: `calc(100% - ${shouldShowNavbar ? '60px' : '0px'})`,
          marginTop: `${shouldShowNavbar ? '60px' : '0px'}`,
          overflow: 'hidden',
          backgroundColor: 'background.default',
        }}
      >
        {shouldShowNavbar && <Navbar />}

        <Box
          component="main"
          className={isXPLR ? 'no-scroll' : ''}
          sx={{
            flex: '0 1 auto',
            minHeight: 0,
            maxHeight: '100%',
            scrollbarWidth: 'none',
            marginTop: isMobile ? 1 : 0,
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {/* Toaster and Providers are now mounted once in main.tsx */}
          <Routes />
        </Box>
        <Overlay open={isOpen} onClose={() => dispatch(closeDiagram())} showCloseButton={false}>
          <DiagramBpmnIndex />
        </Overlay>
      </Box>
    </ThemeProvider>
  );
};

export default AppLayout;
